import React, { useState, useEffect, useCallback } from 'react';
import { recordsApi } from '../../api/records';
import api from '../../api/client';
import WidgetCard from './WidgetCard';
import WidgetModal from './WidgetModal';
import { Users, Crown, ArrowRight, Check, X, Search } from 'lucide-react';
import toast from 'react-hot-toast';

const COLORS = ['#3B82F6','#EF4444','#10B981','#F59E0B','#8B5CF6','#EC4899','#14B8A6','#F97316'];
function getUserColor(id) {
  let hash = 0;
  for (let i = 0; i < (id || '').length; i++) hash = ((hash << 5) - hash) + id.charCodeAt(i);
  return COLORS[Math.abs(hash) % COLORS.length];
}
function getInitials(u) {
  return ((u.first_name?.[0] || '') + (u.last_name?.[0] || '')).toUpperCase();
}

export default function TeamWidget({ record }) {
  const [team, setTeam] = useState(null);
  const [members, setMembers] = useState([]);
  const [assignedTo, setAssignedTo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [changing, setChanging] = useState(false);
  const [teams, setTeams] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [teamSearch, setTeamSearch] = useState('');
  const [saving, setSaving] = useState(false);

  const loadTeam = useCallback(async () => {
    if (!record?.id) return;
    try {
      const data = await recordsApi.getRecordTeam(record.id);
      setTeam(data.team);
      setMembers(data.members || []);
      setAssignedTo(data.assigned_to);
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  }, [record?.id]);

  useEffect(() => { loadTeam(); }, [loadTeam]);

  const loadTeams = async () => {
    setTeamsLoading(true);
    try {
      const res = await api.get('/teams');
      setTeams(res.data.teams || []);
    } catch (e) { /* ignore */ }
    finally { setTeamsLoading(false); }
  };

  const handleStartChange = () => {
    setChanging(true);
    setTeamSearch('');
    loadTeams();
  };

  const handleAssignTeam = async (teamId) => {
    setSaving(true);
    try {
      const data = await recordsApi.assignRecordTeam(record.id, teamId);
      setTeam(data.team);
      setMembers(data.members || []);
      setChanging(false);
      toast.success(teamId ? 'Team assigned' : 'Team removed');
    } catch (e) {
      toast.error('Failed to assign team');
    }
    finally { setSaving(false); }
  };

  if (loading) return null;

  const leads = members.filter(m => m.is_lead);
  const nonLeads = members.filter(m => !m.is_lead);

  const filteredTeams = teams.filter(t => {
    if (!teamSearch.trim()) return true;
    return t.name.toLowerCase().includes(teamSearch.toLowerCase());
  });

  const MemberRow = ({ m, compact }) => {
    const isAssigned = m.user_id === assignedTo;
    return (
      <div className={`flex items-center gap-2.5 ${compact ? 'py-1.5 px-3' : 'py-2 px-4'} ${isAssigned ? 'bg-blue-50/60' : 'hover:bg-gray-50'} rounded-lg`}>
        <div className={`${compact ? 'h-6 w-6 text-[10px]' : 'h-8 w-8 text-xs'} rounded-full flex items-center justify-center text-white font-bold flex-shrink-0`}
          style={{ backgroundColor: getUserColor(m.user_id) }}>
          {getInitials(m)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className={`${compact ? 'text-xs' : 'text-sm'} font-medium text-gray-900 truncate`}>
              {m.first_name} {m.last_name}
            </span>
            {m.is_lead && <Crown className="h-3 w-3 text-amber-500 flex-shrink-0" />}
          </div>
          <div className="flex items-center gap-1.5">
            {m.role_name && (
              <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full"
                style={{ backgroundColor: (m.role_color || '#6B7280') + '20', color: m.role_color || '#6B7280' }}>
                {m.role_name}
              </span>
            )}
            {isAssigned && <span className="text-[10px] text-blue-600 font-medium">ASSIGNED</span>}
          </div>
        </div>
      </div>
    );
  };

  // Team picker view (shown in modal when changing team)
  const TeamPicker = () => (
    <div className="p-4">
      <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 mb-3">
        <Search className="h-4 w-4 text-gray-400" />
        <input value={teamSearch} onChange={e => setTeamSearch(e.target.value)}
          placeholder="Search teams..." autoFocus
          className="flex-1 text-sm bg-transparent outline-none placeholder-gray-400" />
      </div>
      {teamsLoading && <div className="text-center text-xs text-gray-400 py-6">Loading teams...</div>}
      {!teamsLoading && filteredTeams.length === 0 && (
        <div className="text-center text-xs text-gray-400 py-6">No teams found</div>
      )}
      <div className="space-y-1 max-h-[300px] overflow-y-auto">
        {filteredTeams.map(t => {
          const isCurrent = t.id === team?.id;
          return (
            <button key={t.id} onClick={() => !isCurrent && handleAssignTeam(t.id)} disabled={saving || isCurrent}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors
                ${isCurrent ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-50 border border-transparent'}
                ${saving ? 'opacity-50' : ''}`}>
              <div className="h-8 w-8 rounded-lg flex items-center justify-center text-white text-xs font-bold"
                style={{ backgroundColor: t.color || '#3B82F6' }}>
                <Users className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900">{t.name}</p>
                <p className="text-[10px] text-gray-400">{t.member_count || 0} members · {t.record_count || 0} records</p>
              </div>
              {isCurrent && <Check className="h-4 w-4 text-blue-500 flex-shrink-0" />}
            </button>
          );
        })}
      </div>
      {team && (
        <button onClick={() => handleAssignTeam(null)} disabled={saving}
          className="w-full mt-3 text-sm text-red-500 hover:text-red-600 hover:bg-red-50 rounded-lg py-2 transition-colors disabled:opacity-50">
          Remove team assignment
        </button>
      )}
      <button onClick={() => setChanging(false)}
        className="w-full mt-1 text-sm text-gray-400 hover:text-gray-600 py-1.5">
        Cancel
      </button>
    </div>
  );

  return (
    <>
      <WidgetCard title="Team" icon="👥" count={members.length}
        onExpand={() => setModalOpen(true)}
        headerAction={
          <button onClick={(e) => { e.stopPropagation(); handleStartChange(); setModalOpen(true); }}
            className="text-[10px] text-blue-500 hover:text-blue-600 font-medium">
            {team ? 'Change' : 'Assign'}
          </button>
        }>
        {!team ? (
          <div className="py-4 text-center">
            <p className="text-xs text-gray-400 mb-2">No team assigned</p>
            <button onClick={() => { handleStartChange(); setModalOpen(true); }}
              className="text-xs text-blue-500 hover:text-blue-600 font-medium">
              Assign a team
            </button>
          </div>
        ) : (
          <div className="py-1">
            <div className="px-3 py-1.5 flex items-center gap-2">
              <div className="h-5 w-5 rounded flex items-center justify-center"
                style={{ backgroundColor: (team.color || '#3B82F6') + '20' }}>
                <Users className="h-3 w-3" style={{ color: team.color || '#3B82F6' }} />
              </div>
              <span className="text-xs font-medium text-gray-700">{team.name}</span>
            </div>
            {leads.slice(0, 1).map(m => <MemberRow key={m.user_id} m={m} compact />)}
            {nonLeads.slice(0, 2).map(m => <MemberRow key={m.user_id} m={m} compact />)}
            {members.length > 3 && (
              <p className="text-[10px] text-gray-400 text-center py-1">+{members.length - 3} more</p>
            )}
          </div>
        )}
      </WidgetCard>

      <WidgetModal open={modalOpen} onClose={() => { setModalOpen(false); setChanging(false); }}
        title={changing ? 'Assign Team' : (team ? `Team: ${team.name}` : 'Team')} icon="👥">
        {changing ? (
          <TeamPicker />
        ) : (
          <div className="p-4">
            {!team ? (
              <div className="text-center py-6">
                <Users className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500 mb-3">No team assigned to this record</p>
                <button onClick={handleStartChange}
                  className="text-sm bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors">
                  Assign a Team
                </button>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="h-8 w-8 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: (team.color || '#3B82F6') + '20' }}>
                      <Users className="h-4 w-4" style={{ color: team.color || '#3B82F6' }} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{team.name}</p>
                      <p className="text-[10px] text-gray-400">{members.length} member{members.length !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                  <button onClick={handleStartChange}
                    className="text-xs text-blue-500 hover:text-blue-600 font-medium flex items-center gap-1">
                    <ArrowRight className="h-3 w-3" /> Change Team
                  </button>
                </div>

                {leads.length > 0 && (
                  <div className="mb-3">
                    <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold px-2 mb-1">Team Leads</p>
                    {leads.map(m => <MemberRow key={m.user_id} m={m} />)}
                  </div>
                )}
                {nonLeads.length > 0 && (
                  <div>
                    <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold px-2 mb-1">Members</p>
                    {nonLeads.map(m => <MemberRow key={m.user_id} m={m} />)}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </WidgetModal>
    </>
  );
}
